import { DoneRounded, TranslateRounded } from '@mui/icons-material'
import { Drawer, Menu, MenuItem, Tooltip, styled } from '@mui/material'
import moment from 'moment'
import { MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { setGlobalLanguage } from '../../../../redux/actions/global'
import { IRootState } from '../../../../redux/types'
import useScreenWidth from '../../hooks/useScreenWidth'
import ButtonNavbar from '../../styles/ButtonNavbar'
import 'moment/locale/es'
import 'moment/locale/fr'

export const LanguageDrawer = styled(Drawer)(({ theme }) => ({
  '&.MuiDrawer-paper': {
    backgroundColor: 'transparent',
    marginBottom: 92,
    width: 180,
    borderRadius: '10px',
    [theme.breakpoints.down('md')]: {
      width: '100vw'
    }
  }
}))

export const ContainerActions = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(52, 54, 56, 0.73)',
  backdropFilter: 'blur(15px)',
  borderRadius: '10px',
  '& .MuiListItemIcon-root': {
    minWidth: 'auto',
    marginRight: ' 6px',
    color: theme.palette.grey[100]
  },
  '& .MuiMenuItem-root': {
    height: 56,
    alignItems: 'center',
    color: theme.palette.grey[100],
    fontSize: 16,
    justifyContent: 'center',
    borderBottom: `1px solid ${theme.palette.grey[100]}`
  },
  [theme.breakpoints.down('md')]: {
    '& li:last-child': {
      borderBottom: 'none',
      color: theme.palette.error.main
    },
    '&.filter': {
      '& li:last-child': {
        color: theme.palette.grey[100]
      }
    }
  }
}))

function LanguageFilter({ theme }: { theme: string }) {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const isSmallMobile = useScreenWidth(380)
  const isMobile = useScreenWidth(700)
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(search)

  const [anchorLan, setAnchorLan] = useState<null | HTMLElement>(null)
  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { globalLanguageId } = useSelector((state: IRootState) => state.global)
  const [mobileOpen, setMobileOpen] = useState<null | HTMLElement>(null)

  const handleClickLan = (event: MouseEvent<HTMLElement>) => {
    setAnchorLan(event.currentTarget)
  }

  const handleMobileClose = () => {
    setMobileOpen(null)
  }

  const handleClickMobile = (event: MouseEvent<HTMLElement>) => {
    setMobileOpen(event.currentTarget)
  }

  const handleGlobalLanguage = (languageId: number, languageCode: string) => {
    localStorage.setItem('globalLanguageId', languageId.toString())
    localStorage.setItem('globalLanguageCode', languageCode)
    dispatch(setGlobalLanguage(languageId))
    i18n.changeLanguage(languageCode)
    searchParams.set('globalLanguageId', languageId.toString())
    navigate(`${pathname}?${searchParams}`, { replace: true })
    moment.locale(languageCode)
  }

  useEffect(() => {
    const languageCode = localStorage.getItem('globalLanguageCode')
    moment.locale(languageCode ?? 'es')
  }, [])

  useEffect(() => {
    if (search && searchParams.get('globalLanguageId') !== null && globalLanguageId !== Number(searchParams.get('globalLanguageId'))) {
      const searchParamLanguage = authUser.languages_name.find(
        (language: any) => Number(language.id) === Number(searchParams.get('globalLanguageId'))
      )
      if (searchParamLanguage) {
        handleGlobalLanguage(searchParamLanguage.id, searchParamLanguage.code)
      } else {
        searchParams.set('globalLanguageId', authUser.languages[0])
        navigate(`${pathname}?${searchParams}`, { replace: true })
      }
    } else if (authUser !== null && authUser.languages_name.length > 0) {
      const languageCodeFound = authUser.languages_name.find((language: any) => language.id == localStorage.getItem('globalLanguageId'))
      if (languageCodeFound) {
        i18n.changeLanguage(languageCodeFound.code)
      } else {
        alert('No tienes permiso para acceder a este lenguaje, se asignará el primer lenguaje disponible.')
        console.error(`No tienes permiso para acceder a este lenguaje, se asignará el primer lenguaje disponible. USER: ${authUser.id}`)
        try {
          i18n.changeLanguage(authUser.languages_name[0].code)
        } catch (error) {
          alert('Ha ocurrido un error inesperado, contacta con un administrador.')
          console.error(error)
        }
      }
    }
  }, [authUser])

  return (
    <>
      {globalLanguageId !== null && authUser.languages_name.find((language: any) => language.id === globalLanguageId) !== undefined && (
        <>
          {isSmallMobile ? (
            <></>
          ) : (
            <Tooltip title="idioma">
              <ButtonNavbar
                aria-controls="language-menu"
                aria-haspopup="true"
                className={theme}
                onClick={isMobile ? handleClickMobile : handleClickLan}
              >
                <TranslateRounded fontSize="small" />
              </ButtonNavbar>
            </Tooltip>
          )}

          {isMobile ? (
            <LanguageDrawer
              variant="temporary"
              anchor="bottom"
              ModalProps={{ keepMounted: true }}
              open={Boolean(mobileOpen)}
              onClose={handleMobileClose}
              onClick={handleMobileClose}
            >
              <ContainerActions className="filter">
                {authUser.languages_name.map((language: any) => (
                  <MenuItem
                    sx={theme => ({
                      gap: 4,
                      '& svg': {
                        color: theme.palette.success.main
                      }
                    })}
                    key={language.id}
                    onClick={() => {
                      handleGlobalLanguage(language.id, language.code)
                      handleMobileClose()
                    }}
                  >
                    {language.id === globalLanguageId && <DoneRounded fontSize="small" />}
                    {language.code.toUpperCase()}
                  </MenuItem>
                ))}
              </ContainerActions>
            </LanguageDrawer>
          ) : (
            <Menu
              id="language-menu"
              anchorEl={anchorLan}
              keepMounted
              open={Boolean(anchorLan)}
              onClose={() => setAnchorLan(null)}
              onClick={() => setAnchorLan(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
            >
              {authUser.languages_name.map((language: any) => (
                <MenuItem
                  sx={theme => ({
                    gap: 4,
                    '& svg': {
                      color: theme.palette.success.main
                    }
                  })}
                  key={language.id}
                  onClick={() => handleGlobalLanguage(language.id, language.code)}
                >
                  {language.code.toUpperCase()}
                  {language.id === globalLanguageId && <DoneRounded fontSize="small" />}
                </MenuItem>
              ))}
            </Menu>
          )}
        </>
      )}
    </>
  )
}

export default LanguageFilter
